import DOMPurify from 'dompurify';

/**
 * Sanitiza HTML de autoria de usuário para renderizar com `v-html`.
 *
 * ---------------------------------------------------------------------------
 * ALLOWLIST, NÃO DENYLIST
 * ---------------------------------------------------------------------------
 * É a única razão desta função existir em vez de chamar `DOMPurify.sanitize()`
 * direto: o padrão do DOMPurify permite quase todo HTML seguro, e aqui o
 * conteúdo tem uma origem conhecida e estreita — o editor da casa
 * (`InputRichText`, TipTap). Aceitar só o que ele produz reduz a superfície ao
 * que de fato precisa existir.
 *
 * `img` NÃO está na lista, de propósito: imagem neste projeto é anexo com URL
 * no Blob, não conteúdo embutido — deixar `img` passar reabriria o base64 no
 * corpo por outra porta.
 *
 * ---------------------------------------------------------------------------
 * UMA PASSAGEM BASTA — E A HISTÓRIA DE POR QUE JÁ FORAM CINCO
 * ---------------------------------------------------------------------------
 * A primeira versão desta função repetia a limpeza até o ponto fixo, defendendo
 * um suposto furo do dompurify 3.4.5: `sanitize()` deixaria passar os atributos
 * dos elementos que viessem DEPOIS de um `<script>`.
 *
 * Esse furo não existe NO NAVEGADOR. Ele foi medido sob **happy-dom**, que é o
 * `environment` do vitest aqui — e happy-dom não implementa o DOM com fidelidade
 * suficiente para o DOMPurify funcionar. Mesma entrada, mesma versão 3.4.5:
 *
 *   entrada:  '<p>oi</p><script>1</script><img src=x onerror="alert(1)">'
 *   Chromium: '<p>oi</p><img src="x">'                        limpo
 *   jsdom:    '<p>oi</p><img src="x">'                        limpo
 *   happy-dom:'<p>oi</p><img src="x" onerror="alert(1)">'     handler intacto
 *
 * `DOMPurify.isSupported` é `true` nos três — happy-dom não avisa que não dá
 * conta, só devolve resultado errado.
 *
 * A quebra do happy-dom é SELETIVA, e é isso que a torna traiçoeira: a mesma
 * entrada SEM o `<script>` ele limpa certo. Só falha quando há um `<script>`
 * antes — que é justamente o que um atacante escreveria, e por isso o achado
 * pareceu tão convincente.
 *
 * A CONSEQUÊNCIA PARA QUEM FOR ESCREVER TESTE AQUI: um teste de sanitização
 * escrito no environment padrão mede um DOM que não sanitiza, e o resultado não
 * vale nas duas direções — nem para aprovar, nem para reprovar. Por isso o spec
 * deste arquivo declara `@vitest-environment jsdom` na primeira linha. Se você
 * remover essa linha, os testes continuam "passando" e param de significar
 * qualquer coisa.
 */

const TAGS_PERMITIDAS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li',
  'a', 'code', 'pre', 'blockquote',
  'h1', 'h2', 'h3',
  'span', 'div',
  // Os planos que o agente de chamados escreve no fio (markdown convertido
  // pelo adaptador do Steward — docs/plans/steward-auto-plan.md, A7): tabelas,
  // um quarto nível de título, separador e o plano recolhido em `details`.
  // Nenhum atributo novo entra com eles.
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'h4', 'hr',
  'details', 'summary',
];

const ATRIBUTOS_PERMITIDOS = ['href', 'target', 'rel'];

export function sanitizeRichText(html?: string | null): string {
  return DOMPurify.sanitize(String(html ?? ''), {
    ALLOWED_TAGS: TAGS_PERMITIDAS,
    ALLOWED_ATTR: ATRIBUTOS_PERMITIDOS,
  });
}
